import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../contexts/ToastContext';
import { useConfirm } from '../contexts/ConfirmContext';
import axios from 'axios';
import MFASetup from '../components/MFASetup';
import UserAvatar from '../components/UserAvatar';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5001';

interface MFAStatus {
  mfaEnabled: boolean;
  mfaVerifiedAt: string | null;
  backupCodesCount: number;
}

const Profile: React.FC = () => {
  const { user } = useAuth();
  const { showToast } = useToast();
  const { confirm } = useConfirm();
  const [mfaStatus, setMfaStatus] = useState<MFAStatus | null>(null);
  const [showMFASetup, setShowMFASetup] = useState(false);
  const [showDisableMFA, setShowDisableMFA] = useState(false);
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmNewPassword, setConfirmNewPassword] = useState('');
  const [pwLoading, setPwLoading] = useState(false);
  const [pwError, setPwError] = useState('');
  const [pwSuccess, setPwSuccess] = useState('');
  const [profilePicture, setProfilePicture] = useState<string | null>(null);
  const [uploadingImage, setUploadingImage] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    fetchMFAStatus();
    if (user?.profilePicture) {
      setProfilePicture(user.profilePicture);
    }
  }, [user]);

  const fetchMFAStatus = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/api/mfa/status`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.data.success) {
        setMfaStatus(response.data.data);
      }
    } catch (err: any) {
      console.error('Failed to fetch MFA status:', err);
    }
  };

  const handleEnableMFA = () => {
    setShowMFASetup(true);
  };

  const handleMFASetupComplete = () => {
    setShowMFASetup(false);
    fetchMFAStatus(); // Refresh MFA status
    setSuccess('Two-factor authentication has been enabled successfully!');
    setTimeout(() => setSuccess(''), 5000);
  };

  const handleDisableMFA = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    setLoading(true);

    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${API_URL}/api/mfa/disable`,
        { password },
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      if (response.data.success) {
        setSuccess('Two-factor authentication has been disabled.');
        setShowDisableMFA(false);
        setPassword('');
        fetchMFAStatus();
        setTimeout(() => setSuccess(''), 5000);
      }
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to disable MFA');
    } finally {
      setLoading(false);
    }
  };

  const handleRegenerateBackupCodes = async () => {
    const confirmedRegen = await confirm({
      title: 'Regenerate Backup Codes',
      message: 'Are you sure you want to regenerate backup codes? This will invalidate all existing backup codes.',
      confirmLabel: 'Regenerate',
      variant: 'danger'
    });
    if (!confirmedRegen) {
      return;
    }

    const password = window.prompt('Enter your password to confirm:');
    if (!password) return;

    try {
      setLoading(true);
      setError('');

      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${API_URL}/api/mfa/regenerate-backup-codes`,
        { password },
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      if (response.data.success) {
        const codes = response.data.data.backupCodes;

        // Display codes in an alert (in production, show in a modal)
        showToast('success', ['New backup codes generated:', ...codes, 'Please save these codes in a safe place!']);

        // Download codes
        const content = 'IPRI MFA Backup Codes\n\n' + codes.join('\n');
        const blob = new Blob([content], { type: 'text/plain' });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = 'ipri-mfa-backup-codes.txt';
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        window.URL.revokeObjectURL(url);

        fetchMFAStatus();
        setSuccess('Backup codes regenerated successfully!');
        setTimeout(() => setSuccess(''), 5000);
      }
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to regenerate backup codes');
    } finally {
      setLoading(false);
    }
  };

  const handleProfilePictureClick = () => {
    fileInputRef.current?.click();
  };

  const handleProfilePictureChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      setError('Please select an image file');
      return;
    }

    // Validate file size (5MB max)
    if (file.size > 5 * 1024 * 1024) {
      setError('Image size must be less than 5MB');
      return;
    }

    try {
      setUploadingImage(true);
      setError('');

      const formData = new FormData();
      formData.append('profilePicture', file);

      const token = localStorage.getItem('token');
      const response = await axios.post(`${API_URL}/api/users/profile-picture`, formData, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });

      if (response.data.success) {
        setProfilePicture(response.data.data.profilePicture);

        // Update user in localStorage
        const userStr = localStorage.getItem('user');
        if (userStr) {
          const updatedUser = JSON.parse(userStr);
          updatedUser.profilePicture = response.data.data.profilePicture;
          localStorage.setItem('user', JSON.stringify(updatedUser));
        }

        setSuccess('Profile picture updated successfully!');
        setTimeout(() => setSuccess(''), 3000);

        // Reload page to update sidebar
        window.location.reload();
      }
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to upload profile picture');
    } finally {
      setUploadingImage(false);
    }
  };

  const handleChangePassword = async (e: React.FormEvent) => {
    e.preventDefault();
    setPwError('');
    setPwSuccess('');

    if (!user?.id) return;
    if (newPassword.length < 6) {
      setPwError('New password must be at least 6 characters long');
      return;
    }
    if (newPassword !== confirmNewPassword) {
      setPwError('New passwords do not match');
      return;
    }

    try {
      setPwLoading(true);
      const token = localStorage.getItem('token');
      await axios.put(
        `${API_URL}/api/users/${user.id}/password`,
        { currentPassword, newPassword },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setPwSuccess('Password changed successfully');
      setCurrentPassword('');
      setNewPassword('');
      setConfirmNewPassword('');
      setTimeout(() => setPwSuccess(''), 5000);
    } catch (err: any) {
      setPwError(err.response?.data?.message || 'Failed to change password');
    } finally {
      setPwLoading(false);
    }
  };

  const handleDeleteProfilePicture = async () => {
    const confirmed = await confirm({
      title: 'Delete Profile Picture',
      message: 'Are you sure you want to delete your profile picture?',
      confirmLabel: 'Delete',
      variant: 'danger'
    });
    if (!confirmed) return;

    try {
      setLoading(true);
      setError('');

      const token = localStorage.getItem('token');
      const response = await axios.delete(`${API_URL}/api/users/profile-picture`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.data.success) {
        setProfilePicture(null);

        // Update user in localStorage
        const userStr = localStorage.getItem('user');
        if (userStr) {
          const updatedUser = JSON.parse(userStr);
          updatedUser.profilePicture = null;
          localStorage.setItem('user', JSON.stringify(updatedUser));
        }

        setSuccess('Profile picture deleted successfully!');
        setTimeout(() => setSuccess(''), 3000);

        // Reload page to update sidebar
        window.location.reload();
      }
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to delete profile picture');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="max-w-4xl mx-auto py-8 px-4">
      <h1 className="text-3xl font-bold text-gray-900 dark:text-gray-100 mb-8">Profile Settings</h1>

      {/* Profile Picture */}
      <div className="bg-white dark:bg-gray-800 shadow dark:shadow-gray-900/50 rounded-lg p-6 mb-6">
        <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100 mb-4">Profile Picture</h2>
        <div className="flex items-center gap-6">
          <div className="relative">
            <UserAvatar
              user={{
                firstName: user?.firstName,
                lastName: user?.lastName,
                profilePicture: profilePicture
              }}
              size="xl"
            />
            {uploadingImage && (
              <div className="absolute inset-0 flex items-center justify-center bg-black bg-opacity-50 rounded-full">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-white"></div>
              </div>
            )}
          </div>
          <div className="flex-1">
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              onChange={handleProfilePictureChange}
              className="hidden"
            />
            <button
              onClick={handleProfilePictureClick}
              disabled={uploadingImage}
              className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed mr-3"
            >
              {profilePicture ? 'Change Picture' : 'Upload Picture'}
            </button>
            {profilePicture && (
              <button
                onClick={handleDeleteProfilePicture}
                disabled={loading || uploadingImage}
                className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Delete Picture
              </button>
            )}
            <p className="text-sm text-gray-500 dark:text-gray-400 mt-2">
              JPG, PNG, GIF or WEBP. Max size 5MB.
            </p>
          </div>
        </div>
      </div>

      {/* User Information */}
      <div className="bg-white dark:bg-gray-800 shadow dark:shadow-gray-900/50 rounded-lg p-6 mb-6">
        <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100 mb-4">User Information</h2>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Name</label>
            <p className="mt-1 text-sm text-gray-900 dark:text-gray-100">
              {user?.firstName} {user?.lastName}
            </p>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Email</label>
            <p className="mt-1 text-sm text-gray-900 dark:text-gray-100">{user?.email}</p>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Organization</label>
            <p className="mt-1 text-sm text-gray-900 dark:text-gray-100">{user?.organization || <span className="text-gray-500 italic">Not set</span>}</p>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Role</label>
            <p className="mt-1 text-sm text-gray-900 dark:text-gray-100">{user?.role}</p>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">User ID</label>
            <p className="mt-1 text-sm text-gray-500 dark:text-gray-400 font-mono">{user?.id}</p>
          </div>
          <div className="col-span-2">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Assigned Countries</label>
            {user?.countries && user.countries.length > 0 ? (
              <div className="mt-1 flex flex-wrap gap-1">
                {user.countries.map(c => (
                  <span key={c} className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-emerald-100 text-emerald-800">
                    {c}
                  </span>
                ))}
              </div>
            ) : (
              <p className="mt-1 text-sm text-gray-500 dark:text-gray-400 italic">All countries</p>
            )}
          </div>
        </div>
      </div>

      {/* Change Password */}
      <div className="bg-white dark:bg-gray-800 shadow dark:shadow-gray-900/50 rounded-lg p-6 mb-6">
        <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100 mb-4">Change Password</h2>

        {pwSuccess && (
          <div className="mb-4 p-4 bg-green-100 border border-green-400 text-green-700 rounded">
            {pwSuccess}
          </div>
        )}

        {pwError && (
          <div className="mb-4 p-4 bg-red-100 border border-red-400 text-red-700 rounded">
            {pwError}
          </div>
        )}

        <form onSubmit={handleChangePassword} className="space-y-4 max-w-md">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Current Password</label>
            <input
              type="password"
              value={currentPassword}
              onChange={(e) => setCurrentPassword(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500"
              required
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">New Password</label>
            <input
              type="password"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500"
              minLength={6}
              required
            />
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">Minimum 6 characters.</p>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Confirm New Password</label>
            <input
              type="password"
              value={confirmNewPassword}
              onChange={(e) => setConfirmNewPassword(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500"
              minLength={6}
              required
            />
          </div>
          <button
            type="submit"
            disabled={pwLoading}
            className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {pwLoading ? 'Changing...' : 'Change Password'}
          </button>
        </form>
      </div>

      {/* Two-Factor Authentication */}
      <div className="bg-white dark:bg-gray-800 shadow dark:shadow-gray-900/50 rounded-lg p-6">
        <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100 mb-4">Two-Factor Authentication</h2>

        {success && (
          <div className="mb-4 p-4 bg-green-100 border border-green-400 text-green-700 rounded">
            {success}
          </div>
        )}

        {error && (
          <div className="mb-4 p-4 bg-red-100 border border-red-400 text-red-700 rounded">
            {error}
          </div>
        )}

        {mfaStatus ? (
          <div>
            <div className="flex items-center justify-between mb-4">
              <div>
                <p className="text-sm text-gray-700 dark:text-gray-300 mb-2">
                  Status:{' '}
                  <span
                    className={`font-semibold ${
                      mfaStatus.mfaEnabled ? 'text-green-600' : 'text-gray-500'
                    }`}
                  >
                    {mfaStatus.mfaEnabled ? 'Enabled' : 'Disabled'}
                  </span>
                </p>
                {mfaStatus.mfaEnabled && mfaStatus.mfaVerifiedAt && (
                  <p className="text-xs text-gray-500 dark:text-gray-400">
                    Last verified: {new Date(mfaStatus.mfaVerifiedAt).toLocaleString()}
                  </p>
                )}
              </div>
            </div>

            <p className="text-sm text-gray-600 dark:text-gray-400 mb-6">
              {mfaStatus.mfaEnabled
                ? 'Two-factor authentication adds an extra layer of security to your account by requiring a code from your authenticator app when you sign in.'
                : 'Protect your account with two-factor authentication. This is optional but highly recommended for security.'}
            </p>

            {mfaStatus.mfaEnabled ? (
              <div className="space-y-4">
                <div className="bg-gray-50 dark:bg-gray-900 p-4 rounded">
                  <p className="text-sm text-gray-700 dark:text-gray-300">
                    Backup codes remaining: <span className="font-semibold">{mfaStatus.backupCodesCount}</span>
                  </p>
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                    Backup codes can be used to access your account if you lose your authenticator device.
                  </p>
                </div>

                <div className="flex gap-3">
                  <button
                    onClick={handleRegenerateBackupCodes}
                    disabled={loading}
                    className="px-4 py-2 bg-gray-200 dark:bg-gray-600 text-gray-700 dark:text-gray-300 rounded hover:bg-gray-300 dark:hover:bg-gray-500 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Regenerate Backup Codes
                  </button>
                  <button
                    onClick={() => setShowDisableMFA(true)}
                    disabled={loading}
                    className="px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Disable 2FA
                  </button>
                </div>

                {/* Disable MFA Form */}
                {showDisableMFA && (
                  <div className="border-t pt-4 mt-4">
                    <form onSubmit={handleDisableMFA} className="space-y-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Enter your password to disable two-factor authentication:
                        </label>
                        <input
                          type="password"
                          value={password}
                          onChange={(e) => setPassword(e.target.value)}
                          className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                          placeholder="Password"
                          required
                        />
                      </div>
                      <div className="flex gap-3">
                        <button
                          type="button"
                          onClick={() => {
                            setShowDisableMFA(false);
                            setPassword('');
                            setError('');
                          }}
                          className="px-4 py-2 bg-gray-200 dark:bg-gray-600 text-gray-700 dark:text-gray-300 rounded hover:bg-gray-300 dark:hover:bg-gray-500 transition-colors"
                        >
                          Cancel
                        </button>
                        <button
                          type="submit"
                          disabled={loading}
                          className="px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          {loading ? 'Disabling...' : 'Confirm Disable'}
                        </button>
                      </div>
                    </form>
                  </div>
                )}
              </div>
            ) : (
              <button
                onClick={handleEnableMFA}
                className="px-6 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
              >
                Enable Two-Factor Authentication
              </button>
            )}
          </div>
        ) : (
          <div className="text-center py-4">
            <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
            <p className="text-gray-600 dark:text-gray-400 mt-2">Loading...</p>
          </div>
        )}
      </div>

      {/* MFA Setup Modal */}
      {showMFASetup && (
        <MFASetup
          onClose={() => setShowMFASetup(false)}
          onSuccess={handleMFASetupComplete}
        />
      )}
    </div>
  );
};

export default Profile;
